import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import NavBar from '../components/NavBar';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

function SocialBrowser() {
  const navigate = useNavigate();
  const [moodBoost, setMoodBoost] = useState(false);
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isActive, setIsActive] = useState(false);
  const { isFirstVisit, setVisited } = useFirstVisit('social');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);

  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  useEffect(() => {
    let interval = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      alert('Social media time is up! Time for a mindful break.');
      navigate('/focus');
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft, navigate]);

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function startTimer() {
    setIsActive(true);
  }

  function pauseTimer() {
    setIsActive(false);
  }

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100vh', paddingTop: '60px' }}>
      <NavBar title={'🌐 MindBridge Browser'} />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="social" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      <Card style={{ textAlign: 'center' }}>
        <h1 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '700' }}>🌐 MindBridge Browser</h1>
        <p style={{ color: '#64748b', margin: '0 0 16px 0' }}>
          Browse social media mindfully with built-in wellness features
        </p>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontWeight: '500' }}>Mood Boost Filter</span>
          <label style={{ position: 'relative', display: 'inline-block', width: '60px', height: '34px' }}>
            <input
              type="checkbox"
              checked={moodBoost}
              onChange={(e) => setMoodBoost(e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'absolute',
              cursor: 'pointer',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: moodBoost ? '#4caf50' : '#ccc',
              transition: '0.4s',
              borderRadius: '34px'
            }}>
              <span style={{
                position: 'absolute',
                content: '""',
                height: '26px',
                width: '26px',
                left: moodBoost ? '30px' : '4px',
                bottom: '4px',
                backgroundColor: 'white',
                transition: '0.4s',
                borderRadius: '50%'
              }}></span>
            </span>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontWeight: '500' }}>Session Timer</span>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: timeLeft < 300 ? '#f44336' : '#4caf50' 
          }}>
            {formatTime(timeLeft)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <Button 
            onClick={startTimer} 
            disabled={isActive}
            style={{ flex: 1, backgroundColor: '#4caf50', margin: 0 }}
          >
            {isActive ? 'Timer Active' : 'Start Session'}
          </Button>
          <Button 
            onClick={pauseTimer} 
            disabled={!isActive}
            style={{ flex: 1, backgroundColor: '#ff9800', margin: 0 }}
          >
            Pause
          </Button>
        </div>
      </Card>

      <Card style={{ minHeight: '300px', position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: moodBoost ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {moodBoost ? '🌟' : '📱'}
          </div>
          <h3 style={{ color: '#2c3e50', marginBottom: '8px' }}>
            {moodBoost ? 'Mood Boost Feed Active' : 'Social Media Feed'}
          </h3>
          <p style={{ color: '#64748b', textAlign: 'center', fontSize: '14px' }}>
            {moodBoost 
              ? 'Showing positive, supportive content to boost your mood' 
              : 'Enable Mood Boost filter for a more positive experience'
            }
          </p>
          
          {moodBoost && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px', marginBottom: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '14px', color: '#2c3e50' }}>&quot;You&apos;re doing great! Small steps lead to big changes 💪&quot;</div>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>@PositiveVibesOnly</div>
              </div>
              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '14px', color: '#2c3e50' }}>&quot;Mental health check-in: How are you being kind to yourself today? 🌸&quot;</div>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>@MindfulMoments</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {timeLeft <= 300 && isActive && (
        <Card style={{ backgroundColor: '#fff3e0', border: '2px solid #ff9800' }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ color: '#e65100', margin: '0 0 8px 0', fontWeight: '600' }}>⏰ Time Almost Up!</h3>
            <p style={{ color: '#ef6c00', margin: '0 0 12px 0' }}>
              Consider taking a mindful break soon
            </p>
            <Button 
              onClick={() => navigate('/focus')}
              style={{ backgroundColor: '#ff9800', margin: 0 }}
            >
              Take Break Now
            </Button>
          </div>
        </Card>
      )}

      <div style={{ marginTop: '24px', display: 'flex', gap: '8px' }}>
        <Button onClick={() => navigate('/sleep')} style={{ flex: 1, backgroundColor: '#9c27b0' }}>
          ← Sleep Tracker
        </Button>
        <Button onClick={() => navigate('/focus')} style={{ flex: 1, backgroundColor: '#ff5722' }}>
          Focus Mode →
        </Button>
      </div>
    </div>
  );
}

export default SocialBrowser;
